import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  Upload,
  notification,
  Skeleton
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  BookOutlined,
  DownloadOutlined,
  EyeOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SearchOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';

interface MoodleCourse {
  id: number;
  fullname: string;
  shortname: string;
  idnumber: string;
  categoryid: number;
  categoryname?: string;
  visible: boolean;
  startdate: number;
  enddate: number;
  format: string;
  enrolledusers?: number;
  summary?: string;
}

interface MoodleCategory {
  id: number;
  name: string;
  parent: number;
}

interface MoodleCourseFormData {
  fullname: string;
  shortname: string;
  idnumber: string;
  categoryid: number;
  summary: string;
  format: string;
  visible: boolean;
}

interface EnrolledUser {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  department?: string;
  roles: Array<{
    roleid: number;
    name: string;
    shortname: string;
    sortorder: number;
  }>;
}

interface GradeData {
  id: number;
  fullname: string;
  email: string;
  department: string;
  grades: {
    [key: string]: string;
  };
}

const { confirm } = Modal;

const MoodleCourseManagementPage: React.FC = () => {
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [categories, setCategories] = useState<MoodleCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCourse, setEditingCourse] = useState<MoodleCourse | null>(null);
  const [enrolledUsersModalVisible, setEnrolledUsersModalVisible] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<MoodleCourse | null>(null);
  const [enrolledUsers, setEnrolledUsers] = useState<EnrolledUser[]>([]);
  const [filteredEnrolledUsers, setFilteredEnrolledUsers] = useState<EnrolledUser[]>([]);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [form] = Form.useForm();

  // States for grades modal
  const [gradesModalVisible, setGradesModalVisible] = useState(false);
  const [gradesData, setGradesData] = useState<GradeData[]>([]);
  const [gradeColumns, setGradeColumns] = useState<string[]>([]);
  const [gradesLoading, setGradesLoading] = useState(false);

  // Load dữ liệu khi component mount
  useEffect(() => {
    fetchCourses();
    fetchCategories();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/moodle/courses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCourses(data || []);
      } else {
        throw new Error('Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      message.error('Không thể tải danh sách khóa học');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/v1/moodle/categories', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Không thể tải danh sách danh mục');
    }
  };

  const fetchEnrolledUsers = async (courseId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/moodle/courses/${courseId}/enrolled-users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Extract data from API response wrapper
        const data = result.data || [];
        const usersArray = Array.isArray(data) ? data : [];
        setEnrolledUsers(usersArray);
        setFilteredEnrolledUsers(usersArray);
      } else {
        throw new Error('Failed to fetch enrolled users');
      }
    } catch (error) {
      console.error('Error fetching enrolled users:', error);
      message.error('Không thể tải danh sách học viên');
    } finally {
      setLoading(false);
    }
  };

  const showAddModal = () => {
    setEditingCourse(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (course: MoodleCourse) => {
    setEditingCourse(course);
    form.setFieldsValue({
      fullname: course.fullname,
      shortname: course.shortname,
      idnumber: course.idnumber,
      categoryid: course.categoryid,
      summary: course.summary || '',
      format: course.format || 'topics',
      visible: course.visible
    });
    setModalVisible(true);
  };

  const showEnrolledUsersModal = (course: MoodleCourse) => {
    setSelectedCourse(course);
    setEnrolledUsersModalVisible(true);
    fetchEnrolledUsers(course.id);
  };

  const showGradesModal = (course: MoodleCourse) => {
    setSelectedCourse(course);
    setGradesModalVisible(true);
    fetchCourseGrades(course.id);
  };

  const fetchCourseGrades = async (courseId: number) => {
    try {
      setGradesLoading(true);
      const response = await fetch(`/api/v1/moodle/courses/${courseId}/grades`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setGradesData(result.data || []);
        setGradeColumns(result.columns || []);
      } else {
        throw new Error('Failed to fetch grades');
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      message.error('Không thể tải danh sách điểm');
    } finally {
      setGradesLoading(false);
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    setEditingCourse(null);
  };

  const handleEnrolledUsersCancel = () => {
    setEnrolledUsersModalVisible(false);
    setSelectedCourse(null);
    setEnrolledUsers([]);
    setFilteredEnrolledUsers([]);
    setSearchText('');
    setRoleFilter('');
  };

  const handleGradesCancel = () => {
    setGradesModalVisible(false);
    setGradesData([]);
    setGradeColumns([]);
    setSelectedCourse(null);
  };

  const exportGradesToExcel = () => {
    if (gradesData.length === 0) {
      message.warning('Không có dữ liệu để xuất');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(
      gradesData.map(item => ({
        'ID': item.id,
        'Họ tên': item.fullname,
        'Email': item.email,
        'Lớp/Ngành': item.department,
        ...item.grades
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Điểm khóa học');
    XLSX.writeFile(workbook, `Diem_${selectedCourse?.shortname || 'course'}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Filter and search functions for enrolled users
  const handleSearchChange = (value: string) => {
    setSearchText(value);
    filterEnrolledUsers(value, roleFilter);
  };

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    filterEnrolledUsers(searchText, value);
  };

  const filterEnrolledUsers = (search: string, role: string) => {
    let filtered = enrolledUsers;

    // Filter by search text
    if (search) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(search.toLowerCase()) ||
        user.firstname.toLowerCase().includes(search.toLowerCase()) ||
        user.lastname.toLowerCase().includes(search.toLowerCase()) ||
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        (user.department && user.department.toLowerCase().includes(search.toLowerCase()))
      );
    }

    // Filter by role
    if (role) {
      filtered = filtered.filter(user =>
        user.roles && user.roles.some(r => r.shortname === role)
      );
    }

    setFilteredEnrolledUsers(filtered);
  };

  // Get unique roles for filter options
  const getUniqueRoles = () => {
    const allRoles = enrolledUsers.flatMap(user => user.roles || []);
    const uniqueRoles = allRoles.filter((role, index, self) =>
      index === self.findIndex(r => r.shortname === role.shortname)
    );
    return uniqueRoles;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const url = editingCourse ? `/api/v1/moodle/courses` : '/api/v1/moodle/courses';
      const method = editingCourse ? 'PUT' : 'POST';
      const payload = editingCourse ? { ...editingCourse, ...values } : values;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        message.success(editingCourse ? 'Cập nhật khóa học thành công' : 'Tạo khóa học thành công');
        setModalVisible(false);
        setEditingCourse(null);
        form.resetFields();
        fetchCourses();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error submitting course:', error);
      message.error('Không thể lưu khóa học');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (courseId: number) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/moodle/courses/${courseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        message.success('Xóa khóa học thành công');
        fetchCourses();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error deleting course:', error);
      message.error('Không thể xóa khóa học');
    } finally {
      setLoading(false);
    }
  };

  const handleUnenrolUser = async (userId: number) => {
    if (!selectedCourse) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/v1/moodle/courses/${selectedCourse.id}/users/${userId}/unenrol`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('quanly_truonghoc_token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          if (result.warning) {
            message.warning(result.warning);
          } else {
            message.success('Hủy ghi danh thành công');
          }
          fetchEnrolledUsers(selectedCourse.id);
        } else {
          throw new Error(result.error || 'Có lỗi xảy ra');
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Có lỗi xảy ra');
      }
    } catch (error: any) {
      console.error('Error unenrolling user:', error);
      message.error(`Không thể hủy ghi danh: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    const exportData = courses.map(course => ({
      'ID': course.id,
      'Tên khóa học': course.fullname,
      'Tên ngắn': course.shortname,
      'Mã số': course.idnumber,
      'Danh mục': course.categoryname || 'N/A',
      'Trạng thái': course.visible ? 'Hiển thị' : 'Ẩn',
      'Ngày bắt đầu': course.startdate ? new Date(course.startdate * 1000).toLocaleDateString() : 'N/A',
      'Ngày kết thúc': course.enddate ? new Date(course.enddate * 1000).toLocaleDateString() : 'N/A',
      'Định dạng': course.format || 'N/A',
      'Số học viên': course.enrolledusers || 0
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Khóa học Moodle');
    XLSX.writeFile(wb, `moodle_courses_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const courseColumns: ColumnsType<MoodleCourse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên khóa học',
      dataIndex: 'fullname',
      key: 'fullname',
      width: 250,
      sorter: (a, b) => a.fullname.localeCompare(b.fullname),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc tên khóa học"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.fullname.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Tên ngắn',
      dataIndex: 'shortname',
      key: 'shortname',
      width: 150,
      sorter: (a, b) => a.shortname.localeCompare(b.shortname),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc tên ngắn"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.shortname.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Mã số',
      dataIndex: 'idnumber',
      key: 'idnumber',
      width: 120,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc mã số"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => (record.idnumber || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Danh mục',
      key: 'categoryname',
      width: 150,
      render: (_, record) => {
        const category = categories.find(cat => cat.id === record.categoryid);
        return category ? category.name : 'N/A';
      },
      filters: categories.map(cat => ({ text: cat.name, value: cat.id })),
      onFilter: (value, record) => record.categoryid === value,
    },
    {
      title: 'Trạng thái',
      key: 'visible',
      width: 100,
      render: (_, record) => (
        <Tag color={record.visible ? 'green' : 'red'}>
          {record.visible ? 'Hiển thị' : 'Ẩn'}
        </Tag>
      ),
      filters: [
        { text: 'Hiển thị', value: true },
        { text: 'Ẩn', value: false },
      ],
      onFilter: (value, record) => record.visible === value,
    },
    {
      title: 'Ngày bắt đầu',
      key: 'startdate',
      width: 120,
      render: (_, record) =>
        record.startdate ? new Date(record.startdate * 1000).toLocaleDateString() : 'N/A',
    },
    {
      title: 'Học viên',
      key: 'enrolledusers',
      width: 100,
      sorter: (a, b) => (a.enrolledusers || 0) - (b.enrolledusers || 0),
      render: (_, record) => record.enrolledusers || 0,
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Xem học viên">
            <Button
              type="text"
              icon={<UserOutlined />}
              onClick={() => showEnrolledUsersModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Sửa thông tin">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showEditModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Xem điểm số">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              onClick={() => showGradesModal(record)}
              size="small"
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc chắn muốn xóa khóa học này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Tooltip title="Xóa khóa học">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Enrolled users columns with built-in filters
  const enrolledUsersColumns: ColumnsType<EnrolledUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Tên đăng nhập',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      sorter: (a, b) => a.username.localeCompare(b.username),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc tên đăng nhập"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.username.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Họ tên',
      key: 'fullname',
      width: 200,
      sorter: (a, b) => `${a.lastname} ${a.firstname}`.localeCompare(`${b.lastname} ${b.firstname}`),
      render: (_, record) => `${record.lastname} ${record.firstname}`,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc họ tên"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) =>
        `${record.lastname} ${record.firstname}`.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: (a, b) => a.email.localeCompare(b.email),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc email"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => record.email.toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Phòng ban',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      sorter: (a, b) => (a.department || '').localeCompare(b.department || ''),
      render: (department) => department || 'Chưa xác định',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Lọc phòng ban"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Lọc
            </Button>
            <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: 90 }}>
              Xóa
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
      onFilter: (value, record) => (record.department || '').toLowerCase().includes(String(value).toLowerCase()),
    },
    {
      title: 'Vai trò',
      key: 'roles',
      width: 150,
      render: (_, record) => (
        <Space wrap>
          {record.roles && record.roles.map(role => (
            <Tag key={role.roleid} color="blue">{role.name}</Tag>
          ))}
        </Space>
      ),
      filters: [
        { text: 'Student', value: 'student' },
        { text: 'Teacher', value: 'teacher' },
        { text: 'Editing teacher', value: 'editingteacher' },
      ],
      onFilter: (value, record) => record.roles?.some(r => r.shortname === value) || false,
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title="Bạn có chắc chắn muốn hủy ghi danh?"
          onConfirm={() => handleUnenrolUser(record.id)}
          okText="Hủy ghi danh"
          cancelText="Hủy"
        >
          <Tooltip title="Hủy ghi danh">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BookOutlined />
          <span>Quản lý khóa học Moodle</span>
        </div>
      }
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddModal}
          >
            Thêm khóa học
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
            disabled={courses.length === 0}
          >
            Xuất Excel
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchCourses()}
          />
        </Space>
      }
    >
      <Table
        columns={courseColumns}
        dataSource={courses}
        rowKey="id"
        loading={loading}
        pagination={{
          total: courses.length,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} khóa học`,
        }}
        scroll={{ x: 1400 }}
        size="small"
      />

      {/* Modal thêm/sửa khóa học */}
      <Modal
        title={editingCourse ? 'Sửa thông tin khóa học' : 'Thêm khóa học mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        confirmLoading={loading}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            format: 'topics',
            visible: true
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="fullname"
                label="Tên khóa học"
                rules={[{ required: true, message: 'Vui lòng nhập tên khóa học' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="shortname"
                label="Tên ngắn"
                rules={[{ required: true, message: 'Vui lòng nhập tên ngắn' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="idnumber"
                label="Mã số"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="categoryid"
                label="Danh mục"
                rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
              >
                <Select
                  showSearch
                  placeholder="Chọn danh mục"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categories.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      {category.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="format"
                label="Định dạng"
              >
                <Select>
                  <Select.Option value="topics">Chủ đề</Select.Option>
                  <Select.Option value="weeks">Tuần</Select.Option>
                  <Select.Option value="social">Xã hội</Select.Option>
                  <Select.Option value="site">Trang web</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="visible"
                label="Trạng thái"
              >
                <Select>
                  <Select.Option value={true}>Hiển thị</Select.Option>
                  <Select.Option value={false}>Ẩn</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="summary"
            label="Mô tả khóa học"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal danh sách học viên */}
      <Modal
        title={`Danh sách học viên - ${selectedCourse?.fullname}`}
        open={enrolledUsersModalVisible}
        onCancel={handleEnrolledUsersCancel}
        footer={[
          <Button key="close" onClick={handleEnrolledUsersCancel}>
            Đóng
          </Button>
        ]}
        width={1200}
      >
        {/* Info bar */}
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          Tổng cộng: <strong>{enrolledUsers.length}</strong> học viên. Sử dụng icon <SearchOutlined style={{ color: '#1890ff' }} /> trên tiêu đề cột để lọc dữ liệu.
        </div>

        {/* Table with Skeleton loading */}
        {loading && enrolledUsers.length === 0 ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <Table
            columns={enrolledUsersColumns}
            dataSource={enrolledUsers}
            rowKey="id"
            loading={{
              spinning: loading && enrolledUsers.length > 0,
              indicator: <LoadingOutlined style={{ fontSize: 24 }} spin />,
              tip: 'Đang cập nhật...'
            }}
            pagination={{
              total: enrolledUsers.length,
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} học viên`,
            }}
            size="small"
            scroll={{ x: 1050, y: 400 }}
            style={{
              transition: 'opacity 0.3s ease',
              opacity: loading ? 0.7 : 1
            }}
          />
        )}
      </Modal>

      {/* Modal danh sách điểm số */}
      <Modal
        title={`Danh sách điểm số - ${selectedCourse?.fullname}`}
        open={gradesModalVisible}
        onCancel={() => setGradesModalVisible(false)}
        footer={[
          <Button key="export" type="primary" onClick={() => exportGradesToExcel()}>
            Xuất Excel
          </Button>,
          <Button key="close" onClick={handleGradesCancel}>
            Đóng
          </Button>,
        ]}
        width={1000}
      >
        <Table
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 80,
              sorter: (a, b) => a.id - b.id,
            },
            {
              title: 'Họ tên',
              dataIndex: 'fullname',
              key: 'fullname',
              width: 200,
              sorter: (a: GradeData, b: GradeData) => a.fullname.localeCompare(b.fullname),
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
              width: 200,
              sorter: (a: GradeData, b: GradeData) => a.email.localeCompare(b.email),
            },
            {
              title: 'Phòng ban',
              dataIndex: 'department',
              key: 'department',
              width: 150,
              sorter: (a: GradeData, b: GradeData) => (a.department || '').localeCompare(b.department || ''),
              render: (department: string) => department || 'Chưa xác định',
            },
            ...gradeColumns.map((col) => ({
              title: col,
              dataIndex: ['grades', col],
              key: col,
              width: 100,
              sorter: (a: GradeData, b: GradeData) => {
                const aValue = parseFloat(a.grades[col]) || 0;
                const bValue = parseFloat(b.grades[col]) || 0;
                return aValue - bValue;
              },
              render: (value: string) => value || '-',
            })),
          ]}
          dataSource={gradesData}
          rowKey="id"
          loading={gradesLoading}
          pagination={{
            total: gradesData.length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} học viên`,
          }}
          size="small"
          scroll={{ x: 1000 }}
        />
      </Modal>
    </Card>
  );
};

export default MoodleCourseManagementPage;
